import { useCallback, useEffect, useState } from 'react';

import { EBadgePosition, EBundlesViewModel } from '@appcharge/shared-ui';
import useBadges from 'api/useBadges';
import { Badge, BundleBadge, EBadgeType } from 'common/contracts';

interface IUseBadgeFormProps {
  publisherId: string | undefined;
  relevantBadgeType: EBadgeType | 'both';
  viewModel: EBundlesViewModel | undefined;
  selectedBadgeId: string | null | undefined;
  initialBadges?: Array<{ badge: Badge }>;
}

interface IUseBadgeFormReturn {
  badges: Array<{ badge: Badge }>;
  emblemBadges: object[];
  ribbonBadges: object[];
  badgeList: BundleBadge[];
  filterRelevantBadges: (badgeType: EBadgeType) => boolean;
  getBadges: ReturnType<typeof useBadges>['getBadges'];
}

/**
 * Manages badge state, filtering, and sync effects shared between
 * Bundles and Promotions forms.
 *
 * @example
 * const { badges, emblemBadges, ribbonBadges, badgeList, filterRelevantBadges } = useBadgeForm({
 *   publisherId,
 *   relevantBadgeType,
 *   viewModel,
 *   selectedBadgeId: values.badge
 * });
 */
export const useBadgeForm = ({
  publisherId,
  relevantBadgeType,
  viewModel,
  selectedBadgeId,
  initialBadges
}: IUseBadgeFormProps): IUseBadgeFormReturn => {
  const { getBadges } = useBadges(publisherId);
  const [badges, setBadges] = useState<Array<{ badge: Badge }>>(
    initialBadges || []
  );
  const [emblemBadges, setEmblemBadges] = useState<object[]>([]);
  const [ribbonBadges, setRibbonBadges] = useState<object[]>([]);
  const [badgeList, setBadgeList] = useState<BundleBadge[]>([]);

  const filterRelevantBadges = useCallback(
    (badgeType: EBadgeType): boolean => {
      if (
        relevantBadgeType === 'both' ||
        ((viewModel === EBundlesViewModel.LINEAR ||
          viewModel === EBundlesViewModel.TWO_IN_ONE ||
          viewModel === EBundlesViewModel.TWO_IN_ONE_DYNAMIC ||
          viewModel === EBundlesViewModel.THREE_IN_ONE ||
          viewModel === EBundlesViewModel.SIX_IN_ONE) &&
          badgeType === relevantBadgeType)
      ) {
        return true;
      }
      return false;
    },
    [relevantBadgeType, viewModel]
  );

  // Sync badge state when selected badge changes
  useEffect(() => {
    if (selectedBadgeId) {
      const currentBadge = getBadges?.data?.result?.find(
        (b: Badge): boolean => b.badgeId === selectedBadgeId
      );
      if (currentBadge) {
        setBadges([{ badge: currentBadge }]);
        if (currentBadge?.emblem) {
          setEmblemBadges([currentBadge.emblem]);
        } else {
          setEmblemBadges([]);
        }
        if (currentBadge?.ribbon) {
          setRibbonBadges([currentBadge.ribbon]);
        } else {
          setRibbonBadges([]);
        }
      }
    } else {
      setBadges([]);
      setEmblemBadges([]);
      setRibbonBadges([]);
    }
  }, [selectedBadgeId, getBadges?.data?.result]);

  // Build badge dropdown list from API data
  useEffect(() => {
    if (!getBadges.data?.result) {
      return;
    }
    const badgesDropdownItems: BundleBadge[] = getBadges.data.result
      .map((badge: Badge): BundleBadge | undefined => {
        if (filterRelevantBadges(badge.type)) {
          return {
            content: badge.name,
            name: badge.name,
            key: badge.badgeId || '',
            value: badge.badgeId || '',
            url: badge.emblem?.imageUrl,
            position: badge.position as EBadgePosition
          };
        }
        return undefined;
      })
      .filter((b: BundleBadge | undefined): b is BundleBadge => b !== undefined);
    setBadgeList(badgesDropdownItems);
  }, [getBadges.data, filterRelevantBadges]);

  return {
    badges,
    emblemBadges,
    ribbonBadges,
    badgeList,
    filterRelevantBadges,
    getBadges
  };
};
